import type { BuxusOperatorPayload } from '@buxus/shared'
import {
  idbReadwritePromise,
  resolveIdbDomError,
  toIndexedDbError,
  type IndexedDbOperationMeta,
} from '../errors/webStorageErrorReporting'

const DB_NAME = 'buxus-web-operator-payload'
const STORE = 'operatorPayloads'
const DB_VERSION = 1
const STORE_NAME = 'operator-payloads' as const

export type StoredOperatorPayload = {
  version: string
  payload: BuxusOperatorPayload
}

function keyBase(operatorId: string): string {
  return `v1:${operatorId}:base`
}

function keyStakeholder(operatorId: string, stakeholderCode: string): string {
  return `v1:${operatorId}:sh:${encodeURIComponent(stakeholderCode)}`
}

function idbMeta(
  operation: IndexedDbOperationMeta['operation'],
  extra?: Omit<IndexedDbOperationMeta, 'store' | 'dbName' | 'operation'>,
): IndexedDbOperationMeta {
  return { store: STORE_NAME, dbName: DB_NAME, operation, ...extra }
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION)
      req.onerror = () => {
        reject(
          toIndexedDbError(req.error, 'IndexedDB open failed', idbMeta('open')),
        )
      }
      req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE)
        }
      }
      req.onsuccess = () => resolve(req.result)
    })
  }
  return dbPromise
}

export async function idbPutBase(
  operatorId: string,
  version: string,
  payload: BuxusOperatorPayload,
): Promise<void> {
  const db = await openDb()
  const data: StoredOperatorPayload = { version, payload }
  await idbReadwritePromise(
    db,
    STORE,
    idbMeta('putBase', { operatorId, version }),
    'idb write',
    (store) => store.put(data, keyBase(operatorId)),
  )
}

export async function idbPutStakeholder(
  operatorId: string,
  stakeholderCode: string,
  version: string,
  payload: BuxusOperatorPayload,
): Promise<void> {
  const db = await openDb()
  const data: StoredOperatorPayload = { version, payload }
  await idbReadwritePromise(
    db,
    STORE,
    idbMeta('putStakeholder', { operatorId, stakeholderCode, version }),
    'idb write',
    (store) => store.put(data, keyStakeholder(operatorId, stakeholderCode)),
  )
}

export async function idbGetBase(
  operatorId: string,
  expectedVersion: string,
): Promise<BuxusOperatorPayload | null> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get(keyBase(operatorId))
    req.onsuccess = () => {
      const row = req.result as StoredOperatorPayload | undefined
      if (!row || row.version !== expectedVersion) {
        resolve(null)
        return
      }
      resolve(row.payload)
    }
    req.onerror = () => {
      reject(
        toIndexedDbError(
          resolveIdbDomError(req.error, tx.error),
          'idb read',
          idbMeta('getBase', {
            operatorId,
            version: expectedVersion,
          }),
        ),
      )
    }
  })
}

export async function idbGetStakeholder(
  operatorId: string,
  stakeholderCode: string,
  expectedVersion: string,
): Promise<BuxusOperatorPayload | null> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get(keyStakeholder(operatorId, stakeholderCode))
    req.onsuccess = () => {
      const row = req.result as StoredOperatorPayload | undefined
      if (!row || row.version !== expectedVersion) {
        resolve(null)
        return
      }
      resolve(row.payload)
    }
    req.onerror = () => {
      reject(
        toIndexedDbError(
          resolveIdbDomError(req.error, tx.error),
          'idb read',
          idbMeta('getStakeholder', {
            operatorId,
            stakeholderCode,
            version: expectedVersion,
          }),
        ),
      )
    }
  })
}

/** Discover stakeholder blob keys stored for an operator (for UI after cold reload). */
export async function idbListStakeholderCodes(operatorId: string): Promise<string[]> {
  const db = await openDb()
  const prefix = `v1:${operatorId}:sh:`
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).openKeyCursor()
    const out: string[] = []
    req.onsuccess = () => {
      const cursor = req.result
      if (!cursor) {
        out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        resolve(out)
        return
      }
      const key = cursor.key
      if (typeof key === 'string' && key.startsWith(prefix)) {
        const enc = key.slice(prefix.length)
        try {
          out.push(decodeURIComponent(enc))
        } catch {
          out.push(enc)
        }
      }
      cursor.continue()
    }
    req.onerror = () => {
      reject(
        toIndexedDbError(
          resolveIdbDomError(req.error, tx.error),
          'idb cursor',
          idbMeta('listStakeholders', { operatorId }),
        ),
      )
    }
  })
}

/** Clear cached operator JSON (e.g. on logout). */
export async function idbClearOperatorPayloads(): Promise<void> {
  const db = await openDb()
  await idbReadwritePromise(
    db,
    STORE,
    idbMeta('clearPayloads'),
    'idb clear',
    (store) => store.clear(),
  )
}

/** Delete cached base + stakeholder payloads for one operator. */
export async function idbDeleteOperatorPayloads(operatorId: string): Promise<void> {
  const db = await openDb()
  const base = keyBase(operatorId)
  const stakeholderPrefix = `v1:${operatorId}:sh:`
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    const store = tx.objectStore(STORE)
    const req = store.openKeyCursor()
    let settled = false
    const fail = () => {
      if (settled) return
      settled = true
      reject(
        toIndexedDbError(
          resolveIdbDomError(req.error, tx.error),
          'idb delete',
          idbMeta('deletePayloads', { operatorId }),
        ),
      )
    }
    tx.oncomplete = () => {
      if (settled) return
      settled = true
      resolve()
    }
    tx.onerror = fail
    tx.onabort = fail
    store.delete(base)
    req.onsuccess = () => {
      const cursor = req.result
      if (!cursor) return
      const key = cursor.key
      if (typeof key === 'string' && key.startsWith(stakeholderPrefix)) {
        store.delete(key)
      }
      cursor.continue()
    }
    req.onerror = fail
  })
}
